import type React from 'react';
import { InputText } from 'src/components/InputText';
import type { UseFormReturn } from 'src/hooks';
import type { Summary } from '..';
import { Select } from 'src/components/Select';
import { CorreiosCode } from 'src/model/CorreiosCode';

interface OrderSummaryProps
  extends Pick<UseFormReturn, 'formData' | 'formErrors' | 'onChangeFormInput'> {
  summary: Summary;
  onContinueToPayment: () => void;
  onReturnToShopping: () => void;
  couponCode?: string;
  discount: number;
  totalAfterDiscount?: number;
  fetchCouponDiscount: () => void;
  shippingCost: number;
  selectedDeliveryType: CorreiosCode;
  onSelectDeliveryType: (type: CorreiosCode) => void;
  buyerCep: string;
  sellerCep: string;
}

export enum FormInputCouponDiscount {
  code = 'code',
}

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  formData,
  formErrors,
  summary,
  discount,
  totalAfterDiscount,
  onChangeFormInput,
  onContinueToPayment,
  onReturnToShopping,
  fetchCouponDiscount,
  shippingCost,
  selectedDeliveryType,
  onSelectDeliveryType,
  buyerCep,
  sellerCep,
}) => {
  function isLocalShipping(buyerCep: string, sellerCep: string): boolean {
    const cleanBuyer = buyerCep.replace(/\D/g, '').slice(0, 5);
    const cleanSeller = sellerCep.replace(/\D/g, '').slice(0, 5);
    return cleanBuyer === cleanSeller;
  }

  const localShipping = isLocalShipping(buyerCep, sellerCep);

  const baselineTotal = summary.originalPrice + shippingCost + summary.tax;

  const computedFinalTotal =
    typeof totalAfterDiscount === 'number' &&
    Number.isFinite(totalAfterDiscount)
      ? totalAfterDiscount
      : (summary.total ?? 0) + shippingCost + summary.tax;

  const computedSavings = Math.max(summary.savings ?? 0, 0);

  const totalSavings = summary.productSavings + summary.couponSavings;

  const deliveryTypeOptions = [
    { value: CorreiosCode.SEDEX_CONTRATO, label: 'SEDEX' },
    { value: CorreiosCode.PAC_CONTRATO, label: 'PAC' },
    { value: CorreiosCode.DESVIO_MINI, label: 'DESVIO Mini' },
  ];

  return (
    <div className="w-full bg-white border-none rounded-none shadow-none overflow-visible p-0">
      <div className="bg-gradient-to-r from-gray-50 to-white py-4 border-b border-gray-100">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3 px-4">
          <div className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></div>
          Resumo do Pedido
        </h2>
        <p className="text-sm text-gray-500 mt-1 px-4">
          Confira os detalhes da sua compra
        </p>
      </div>

      <div className="p-4 space-y-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-gray-50">
            <span className="text-gray-600 font-medium">Preço original</span>
            <span className="font-bold text-gray-900 text-lg">
              {summary.originalPrice.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </span>
          </div>

          {summary.productSavings > 0 && (
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-gray-600 font-medium">
                Desconto do Produto
              </span>
              <span className="font-bold text-green-600 text-lg flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                -
                {summary.productSavings.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </div>
          )}

          {summary.couponSavings > 0 && (
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-gray-600 font-medium">
                Desconto do Cupom
              </span>
              <span className="font-bold text-blue-600 text-lg flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                -
                {summary.couponSavings.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </div>
          )}

          {computedSavings > 0 && (
            <div className="flex justify-between items-center py-3 border-b border-gray-50">
              <span className="text-gray-600 font-medium">Economia</span>
              <span className="font-bold text-green-600 text-lg flex items-center gap-1">
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                -
                {computedSavings.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center py-3 border-b border-gray-50">
            <span className="text-gray-600 font-medium">Frete</span>
            <span className="font-bold text-gray-900">
              {shippingCost.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </span>
          </div>

          <div className="mb-4">
            <label className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-2">
              Tipo de Serviço de Frete
            </label>

            {localShipping ? (
              <div className="flex flex-col">
                <div className="text-gray-900 font-bold text-lg">
                  Entrega Local
                </div>
              </div>
            ) : (
              <Select
                name="deliveryType"
                placeholder="Selecione o tipo de frete..."
                value={selectedDeliveryType}
                onChange={(e) =>
                  onSelectDeliveryType(e.target.value as CorreiosCode)
                }
                options={deliveryTypeOptions}
                selectClassName="w-full h-12 px-4 border-2 border-gray-200 rounded-xl bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 text-gray-900 font-medium"
              />
            )}
          </div>

          <div className="flex justify-between items-center py-3 border-b border-gray-50">
            <span className="text-gray-600 font-medium">Imposto</span>
            <span className="font-bold text-gray-900">
              {summary.tax.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </span>
          </div>
        </div>

        {/* <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            Cupom de Desconto
          </h3>
          <div className="space-y-3">
            <InputText
              id="coupon"
              name="code"
              type="text"
              label=""
              value={formData[FormInputCouponDiscount.code]}
              onChange={(e) => {
                onChangeFormInput(FormInputCouponDiscount.code)(e.target.value);
              }}
              placeholder="Digite o código do cupom"
              error={formErrors.code && formErrors.code[0]}
              inputClassName="rounded-xl p-4 w-full border-2 border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 bg-white placeholder-gray-400 text-gray-900 font-medium"
            />

            <button
              type="button"
              onClick={fetchCouponDiscount}
              className="w-full rounded-xl p-4 bg-blue-600 text-white font-bold hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200"
            >
              Aplicar Cupom
            </button>
          </div>
        </div> */}

        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border-2 border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xl font-bold text-gray-900">Total Final</span>
            <span className="text-2xl font-black text-red-600">
              {computedFinalTotal?.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              })}
            </span>
          </div>
        </div>

        <div className="space-y-4 pt-2">
          <button
            onClick={onContinueToPayment}
            className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Finalizar Pagamento
          </button>

          <div className="text-center">
            <button
              onClick={onReturnToShopping}
              className="text-red-600 hover:text-red-700 font-semibold hover:underline transition-all duration-200 flex items-center justify-center gap-2 mx-auto"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Voltar às Compras
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
